import {
	AsyncTaskStatusUpdateEventType,
	GetActiveAsyncTaskSummariesResponseJson,
} from "@performyard/service-contracts";

import internalRequest, { internalStreamRequest } from "./internal-base-request";

/**
 * Stream the authenticated user's own async task (full data, owner-only).
 *
 * @param taskId - The async task id to stream
 * @param signal - Abort signal to cancel the connection
 * @returns A reader over the SSE response body
 */
export const streamUserAsyncTask = async (
	taskId: string,
	signal: AbortSignal
): Promise<ReadableStreamDefaultReader<Uint8Array>> => {
	const response = await internalStreamRequest(`/v1/async-tasks/${taskId}/stream`, { signal });

	if (!response.ok) {
		throw new Error(`Failed to connect to stream: ${response.statusText}`);
	}
	if (!response.body) {
		throw new Error("Response body is null");
	}

	return response.body.getReader();
};

/**
 * Stream a company async task's status (non-sensitive fields only). Use for
 * shared tasks the caller may not own.
 *
 * @param taskId - The async task id to stream
 * @param signal - Abort signal to cancel the connection
 * @returns A reader over the SSE response body
 */
export const streamCompanyAsyncTaskStatus = async (
	taskId: string,
	signal: AbortSignal
): Promise<ReadableStreamDefaultReader<Uint8Array>> => {
	const response = await internalStreamRequest(`/v1/async-tasks/${taskId}/stream/status`, { signal });

	if (!response.ok) {
		throw new Error(`Failed to connect to stream: ${response.statusText}`);
	}
	if (!response.body) {
		throw new Error("Response body is null");
	}

	return response.body.getReader();
};

/**
 * Get active async tasks by name for the authenticated user.
 *
 * @param taskName - The name of the task to search for
 * @returns Array of active tasks matching the given name
 */
export const getActiveUserTasksByName = async (taskName: string): Promise<AsyncTaskStatusUpdateEventType[]> => {
	const [response] = await internalRequest<AsyncTaskStatusUpdateEventType[]>(
		`/v1/async-tasks/user?taskName=${encodeURIComponent(taskName)}`,
		{
			method: "GET",
		}
	);

	return response || [];
};

/**
 * Get active async tasks by name for the company (company-wide).
 *
 * @param taskName - The name of the task to search for
 * @returns Array of active tasks matching the given name
 */
export const getActiveCompanyTasksByName = async (
	taskName: string
): Promise<GetActiveAsyncTaskSummariesResponseJson> => {
	const [response] = await internalRequest<GetActiveAsyncTaskSummariesResponseJson>(
		`/v1/async-tasks/company?taskName=${encodeURIComponent(taskName)}`,
		{
			method: "GET",
		}
	);

	return response || [];
};
